import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { NumberUtils, Tr } from '@utils';
import { PortalRoutePath } from '@models';
import { SimulationTask } from '@services';
import { Grid, IconButton, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

type EntityType = SimulationTask;

export interface IVehicleSuggestionListProps extends IEntityListProps<EntityType> {
	paddingX?: number | string;
	entities: EntityType[];
}

export const VehicleSuggestionList: FC<IVehicleSuggestionListProps> = ({ entities, paddingX, ...props }) => {
	const navigate = useNavigate();

	return (
		<EntityList entities={entities} hideSelectAllButton paddingX={paddingX} {...props}>
			{entities.map((entity, i) => {
				const {
					simulationId,
					id,
					fleetVehicle: {
						carModel: currentCarModel,
						plateNumber: currentPlate,
						segment: carListBand,
						yearlyFuelCost: currentYearlyFuelCost = 0,
						co2Emission: currentCo2Emission = 0,
					} = {},
					taskVehicles = []
				} = entity;

				const selectedVehicle = taskVehicles.find((v) => v.notInSuggestedList) || taskVehicles.find((v) => v.proposed);
				const displayingCarModel = selectedVehicle?.carModel;
				const { model: displayingModel, brand: displayingBrand, version: displayingVersion } = displayingCarModel || {};
				const current = [[currentCarModel?.brand, currentCarModel?.model].filter(Boolean).join(' '), currentPlate]
					.filter(Boolean)
					.join(' - ');
				const displayingCarModelSummary = [[displayingBrand, displayingModel].filter(Boolean).join(' '), displayingVersion]
					.filter(Boolean)
					.join(' - ');

				// TODO: should be used from the displayingCarModel
				const fuelSaving = currentYearlyFuelCost - (selectedVehicle?.fuelCost || 0);
				const co2reduction = currentCo2Emission - (selectedVehicle?.co2emission || 0);

				return (
					<EntityListItem
						key={`entity-${simulationId}-${id}-${i}`}
						sx={{ paddingRight: paddingX }}
						notSelectable
						badge={<Typography variant="body2">{carListBand?.label}</Typography>}
						actionsType="icon"
						actions={[
							{
								icon: (
									<IconButton sx={{ color: 'common.black' }}>
										<ArrowRightAltIcon color="inherit" />
									</IconButton>
								),
								onClick: () => navigate(`/portal/${PortalRoutePath.CONVERSION_LIST}/single-suggestion/${id}`)
							}
						]}
						cardProps={{ sx: { paddingTop: 5, paddingBottom: 2 } }}
					>
						<Grid container columnSpacing={2}>
							<Grid item xs={3}>
								<EntityListItemCell title={<Tr.Portal path="dashboard.vehicle-suggestions.current-vehicle" />}>
									<Typography>{current}</Typography>
								</EntityListItemCell>
							</Grid>
							<Grid item xs={3}>
								<EntityListItemCell title={<Tr.Portal path="dashboard.vehicle-suggestions.voltaage-suggests" />}>
									<Typography>{displayingCarModelSummary}</Typography>
								</EntityListItemCell>
							</Grid>
							<Grid item xs={3}>
								<EntityListItemCell title={<Tr.Portal path="dashboard.vehicle-suggestions.co2-reduced" />}>
									<Typography>{co2reduction ? `${(co2reduction / 1000000).toFixed(2)} ton` : '-'}</Typography>
								</EntityListItemCell>
							</Grid>
							<Grid item xs={3}>
								<EntityListItemCell title={<Tr.Portal path="dashboard.vehicle-suggestions.fuel-reduced" />}>
									<Typography>{NumberUtils.formatAsPrice(fuelSaving, { pureDefault: true })}</Typography>
								</EntityListItemCell>
							</Grid>
						</Grid>
					</EntityListItem>
				);
			})}
		</EntityList>
	);
};
